/**
 * @description Frontend API service for stored card management on Customer Details.
 * Wraps existing paymentSessionService methods for a cleaner import in customer components.
 *
 * @dependencies
 * - paymentSessionService: Underlying API calls (already implemented)
 *
 * @notes
 * - All stored card endpoints already exist in paymentSessionService
 * - This service provides a focused interface for the StoredCardsSection component
 * - Card vaulting uses the HPF flow: create session → HPF capture → vault
 */

import paymentSessionService from './paymentSessionService';

const storedCardsService = {
  /**
   * Get all stored cards for a customer.
   * @param {number} customerId
   * @returns {Promise<{ cards: Array }>}
   */
  getCustomerCards: (customerId) => paymentSessionService.getCustomerCards(customerId),

  /**
   * Set a card as the default for a customer.
   * @param {string} cardId - UUID
   * @param {number} customerId
   * @returns {Promise<{ card: object }>}
   */
  setDefaultCard: (cardId, customerId) => paymentSessionService.setDefaultCard(cardId, customerId),

  /**
   * Remove (deactivate) a stored card.
   * @param {string} cardId - UUID
   * @returns {Promise<{ success: boolean }>}
   */
  removeCard: (cardId) => paymentSessionService.removeCard(cardId),

  /**
   * Get HPF client config for card capture.
   * @returns {Promise<{ publicKey, hpfScriptUrl, environment }>}
   */
  getClientConfig: () => paymentSessionService.getClientConfig(),

  /**
   * Create a payment session for HPF card capture.
   * @param {object} data - { amount, currency, description }
   * @returns {Promise<{ sessionId, sessionUrl, orderId }>}
   */
  createPaymentSession: (data) => paymentSessionService.createPaymentSession(data),

  /**
   * Vault a card captured via HPF.
   * @param {string} sessionId - Payment session ID
   * @param {object} data - { hostedCardId, customerId, customerData }
   * @returns {Promise<{ storedCard: object }>}
   */
  vaultCard: (sessionId, data) => paymentSessionService.vaultCard(sessionId, data)
};

export default storedCardsService;
